import React, { useState } from 'react';
import classNames from 'classnames';
import {
  BsPersonPlusFill,
  BsFiletypeCsv,
  BsBuildingsFill,
  BsCheckCircleFill,
  BsStars,
  BsPlus,
  BsSendFill,
  BsPeopleFill,
  BsXLg,
  BsThreeDotsVertical,
  BsPersonFill,
} from 'react-icons/bs';

import { Dropdown } from '@/components/Dropdown';
import { roleOptions } from '../Onboarding.constants';
import { useTimezones } from '@/hooks/useTimezones';
import styles from '../Onboarding.module.scss';

// ── Constants ────────────────────────────────────────────────

const makeEmptyEmployee = () => ({
  name: '',
  email: '',
  team: '',
  timeZone: '',
  role: '',
  deskId: '',
  employeeId: '',
});

// Compact select style for inline employee rows — strips all default chrome
const inlineSelectStyles = {
  container: {
    border: 'none',
    borderRadius: '0',
    backgroundColor: 'transparent',
  },
  control: {
    height: '26px',
    minHeight: '26px',
    border: 'none',
    borderRadius: '4px',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    padding: 0,
  },
  valueContainer: {
    height: '26px',
    padding: '0',
    backgroundColor: 'transparent',
    borderRadius: '0',
  },
  singleValue: {
    paddingLeft: '0',
    fontSize: '14px',
    color: '#101828',
  },
  input: {
    paddingLeft: '0',
    margin: 0,
    padding: 0,
  },
  placeholder: {
    paddingLeft: '0',
    fontSize: '12px',
    color: '#98A2B3',
  },
};

const IMPORT_METHODS = [
  {
    key: 'invite',
    icon: <BsPersonPlusFill size={22} color="#3363F5" />,
    colorClass: 'featureIconBlue',
    title: 'Invite Employees',
    desc: 'Send invitation links to employees to join Prodaff.',
    enabled: true,
  },
  {
    key: 'csv',
    icon: <BsFiletypeCsv size={22} color="#039855" />,
    colorClass: 'featureIconGreen',
    title: 'Import via CSV',
    desc: 'Upload a CSV file and onboard your entire team at once.',
    enabled: false,
  },
  {
    key: 'integration',
    icon: <BsBuildingsFill size={22} color="#6938EF" />,
    colorClass: 'featureIconPurple',
    title: 'Import from Integrations',
    desc: 'Import users from Google Workspace, Microsoft 365 or other tools.',
    enabled: false,
  },
];

// ── Sub-components ────────────────────────────────────────────

const EmpInlineDropdown = ({
  label,
  value,
  options,
  placeholder,
  onChange,
  isCreatable,
  onCreateOption,
}) => (
  <div className={styles.empInlineField}>
    <span className={styles.empInlineFieldLabel}>{label}</span>
    <Dropdown
      value={value ? { label: value, value } : null}
      options={options}
      selectStyles={inlineSelectStyles}
      placeholder={placeholder}
      isCreatable={isCreatable}
      onChange={(opt) => onChange(opt?.value || '')}
      onCreateOption={onCreateOption}
    />
  </div>
);

const EmployeeRow = ({
  row,
  index,
  timezoneOptions,
  teamOptions,
  onChange,
  onRemove,
  canRemove,
  onAddTeam,
}) => {
  const initial = row.name ? row.name.charAt(0).toUpperCase() : null;

  return (
    <div className={styles.empRow}>
      {/* Avatar */}
      <div
        className={classNames(styles.empAvatar, {
          [styles.empAvatarFilled]: !!initial,
        })}
      >
        {initial || <BsPersonFill size={14} color="#98A2B3" />}
      </div>

      {/* Fields */}
      <div className={styles.empRowContent}>
        {/* Required fields */}
        <div className={styles.empFields}>
          <div className={styles.empInlineField}>
            <span className={styles.empInlineFieldLabel}>Full Name *</span>
            <input
              className={styles.empInlineFieldInput}
              value={row.name}
              onChange={(e) => onChange(index, 'name', e.target.value)}
              placeholder="Enter name"
            />
          </div>

          <div
            className={classNames(
              styles.empInlineField,
              styles.empInlineFieldWide
            )}
          >
            <span className={styles.empInlineFieldLabel}>Email Address *</span>
            <input
              className={styles.empInlineFieldInput}
              value={row.email}
              onChange={(e) => onChange(index, 'email', e.target.value)}
              placeholder="email@example.com"
              type="email"
            />
          </div>

          <EmpInlineDropdown
            label="Department *"
            value={row.team}
            options={teamOptions}
            placeholder="Add / Select"
            isCreatable
            onChange={(val) => onChange(index, 'team', val)}
            onCreateOption={(val) => {
              onChange(index, 'team', val);
              onAddTeam(val);
            }}
          />

          <EmpInlineDropdown
            label="Role *"
            value={row.role}
            options={roleOptions}
            placeholder="Select"
            onChange={(val) => onChange(index, 'role', val)}
          />

          <div
            className={classNames(
              styles.empInlineField,
              styles.empInlineFieldWide
            )}
          >
            <span className={styles.empInlineFieldLabel}>Timezone *</span>
            <Dropdown
              value={
                row.timeZone
                  ? { label: row.timeZone, value: row.timeZone }
                  : null
              }
              options={timezoneOptions}
              selectStyles={inlineSelectStyles}
              placeholder="Select timezone"
              onChange={(opt) => onChange(index, 'timeZone', opt?.value || '')}
            />
          </div>
        </div>

        {/* Optional fields (Desk ID + Employee ID) */}
        <div className={styles.empOptionalFields}>
          <div className={styles.empOptionalField}>
            <span className={styles.empOptionalFieldLabel}>Desk ID:</span>
            <input
              className={styles.empOptionalFieldInput}
              value={row.deskId}
              onChange={(e) => onChange(index, 'deskId', e.target.value)}
              placeholder="Optional"
            />
          </div>
          <div className={styles.empOptionalField}>
            <span className={styles.empOptionalFieldLabel}>Employee ID:</span>
            <input
              className={styles.empOptionalFieldInput}
              value={row.employeeId}
              onChange={(e) => onChange(index, 'employeeId', e.target.value)}
              placeholder="Optional"
            />
          </div>
        </div>
      </div>

      {/* Remove button */}
      {canRemove && (
        <button
          type="button"
          className={styles.empRemoveBtn}
          onClick={() => onRemove(index)}
          aria-label="Remove employee"
        >
          <BsXLg size={11} />
        </button>
      )}
    </div>
  );
};

const InvitedRow = ({ employee }) => {
  const initial = employee.name ? employee.name.charAt(0).toUpperCase() : '?';
  const tags = [employee.team, employee.role, employee.timeZone].filter(
    Boolean
  );

  return (
    <div className={styles.invitedRow}>
      <div className={styles.invitedRowAvatar}>{initial}</div>
      <div className={styles.invitedRowMeta}>
        <span className={styles.invitedRowName}>{employee.name}</span>
        <span className={styles.invitedRowEmail}>{employee.email}</span>
      </div>
      <div className={styles.invitedRowTags}>
        {tags.map((tag) => (
          <span key={tag} className={styles.invitedTag}>
            {tag}
          </span>
        ))}
      </div>
      <div className={styles.invitedRowStatus}>
        <BsCheckCircleFill size={14} />
        <span>Invitation Sent</span>
      </div>
      <button
        type="button"
        className={styles.invitedRowMenu}
        aria-label="Options"
      >
        <BsThreeDotsVertical size={15} />
      </button>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────

export const CreateEmployee = ({ form, setForm, error, onSkip }) => {
  const timezoneOptions = useTimezones();

  const [selectedMethod, setSelectedMethod] = useState('invite');

  // Rows the user is currently filling in
  const [editingRows, setEditingRows] = useState([makeEmptyEmployee()]);

  // Confirmed employees (shown in "Invited" list + synced into form.employees)
  const [invitedList, setInvitedList] = useState(() =>
    form.employees.filter((e) => e.name || e.email)
  );

  // Accumulate team/department options as users create new ones
  const [teamOptions, setTeamOptions] = useState(() => {
    const teams = new Set(form.employees.map((e) => e.team).filter(Boolean));
    return [...teams].map((t) => ({ label: t, value: t }));
  });

  // ── Handlers ──────────────────────────────────────────────

  const updateEditingRow = (index, field, value) => {
    setEditingRows((rows) => {
      const updated = [...rows];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addTeamOption = (value) => {
    setTeamOptions((opts) => {
      if (opts.find((o) => o.value === value)) return opts;
      return [...opts, { label: value, value }];
    });
  };

  const addEditingRow = () => {
    setEditingRows((rows) => [...rows, makeEmptyEmployee()]);
  };

  const removeEditingRow = (index) => {
    setEditingRows((rows) =>
      rows.length === 1
        ? [makeEmptyEmployee()]
        : rows.filter((_, i) => i !== index)
    );
  };

  const handleSendInvitation = () => {
    const valid = editingRows.filter((e) => e.name && e.email);
    if (!valid.length) return;
    const newList = [...invitedList, ...valid];
    setInvitedList(newList);
    setForm({ ...form, employees: newList });
    setEditingRows([makeEmptyEmployee()]);
  };

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className={styles.inviteEmpPage}>
      {/* Import method selector */}
      <div className={styles.importMethods}>
        {IMPORT_METHODS.map((m) => (
          <div
            key={m.key}
            className={classNames(styles.importMethodCard, {
              [styles.importMethodCardSelected]: selectedMethod === m.key,
              [styles.importMethodCardDisabled]: !m.enabled,
            })}
            onClick={() => m.enabled && setSelectedMethod(m.key)}
            role={m.enabled ? 'button' : undefined}
          >
            {selectedMethod === m.key && (
              <BsCheckCircleFill
                size={20}
                color="#3363F5"
                className={styles.importMethodCheck}
              />
            )}
            <div
              className={`${styles.importMethodIconWrap} ${styles[m.colorClass]}`}
            >
              {m.icon}
            </div>
            <h3 className={styles.importMethodTitle}>{m.title}</h3>
            <p className={styles.importMethodDesc}>{m.desc}</p>
          </div>
        ))}
      </div>

      {/* Pro tip banner */}
      <div className={styles.proTipBanner}>
        <BsStars size={15} color="#6938EF" />
        <span>
          <strong>Pro tip:</strong> Most companies invite at least 5 employees
          during setup.
        </span>
        <div className={styles.proTipBtnWrapper}>
          <button type="button" className={styles.proTipBtn}>
            Why is this important? ⓘ
          </button>
          <div className={styles.proTipTooltip} role="tooltip">
            <p className={styles.proTipTooltipHeading}>
              Why invite employees early?
            </p>
            <div className={styles.proTipTooltipItems}>
              <div className={styles.proTipTooltipItem}>
                <span
                  className={`${styles.proTipTooltipDot} ${styles.proTipDotBlue}`}
                />
                <span>
                  Teams onboarded early adopt the tool{' '}
                  <strong>3× faster</strong>
                </span>
              </div>
              <div className={styles.proTipTooltipItem}>
                <span
                  className={`${styles.proTipTooltipDot} ${styles.proTipDotGreen}`}
                />
                <span>
                  Employees start <strong>tracking from day one</strong>, no
                  manual catch-up
                </span>
              </div>
              <div className={styles.proTipTooltipItem}>
                <span
                  className={`${styles.proTipTooltipDot} ${styles.proTipDotPurple}`}
                />
                <span>
                  More members = <strong>richer productivity insights</strong>{' '}
                  for reports
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Employee form */}
      <div className={styles.addEmpSection}>
        <div className={styles.addEmpSectionHeader}>
          <h3 className={styles.addEmpSectionTitle}>Add Employee</h3>
          <p className={styles.addEmpSectionSubtitle}>
            Invite team members one by one.
          </p>
        </div>

        <div className={styles.empFormList}>
          {editingRows.map((row, i) => (
            <EmployeeRow
              key={i}
              row={row}
              index={i}
              timezoneOptions={timezoneOptions}
              teamOptions={teamOptions}
              onChange={updateEditingRow}
              onRemove={removeEditingRow}
              canRemove={editingRows.length > 1}
              onAddTeam={addTeamOption}
            />
          ))}
        </div>

        <div className={styles.empFormActions}>
          <button
            type="button"
            className={styles.addAnotherBtn}
            onClick={addEditingRow}
          >
            <BsPlus size={18} />
            Add Another Employee
          </button>
          <button
            type="button"
            className={styles.sendInviteBtn}
            onClick={handleSendInvitation}
          >
            {/* <BsSendFill size={13} /> */}
            Add Employee
          </button>
        </div>
      </div>

      {/* Invited employees list */}
      {invitedList.length > 0 && (
        <div className={styles.invitedSection}>
          <h3 className={styles.invitedSectionTitle}>
            Added Employees ({invitedList.length})
          </h3>
          <p className={styles.invitedSectionSubtitle}>
            Track the status of your invitations.
          </p>
          <div className={styles.invitedList}>
            {invitedList.map((emp, i) => (
              <InvitedRow key={i} employee={emp} />
            ))}
          </div>
        </div>
      )}

      {/* Validation error */}
      {error && (
        <div className={styles.empError}>
          <pre>{error}</pre>
        </div>
      )}

      {/* Skip banner */}
      <div className={styles.skipBanner}>
        <BsPeopleFill size={18} color="#98A2B3" />
        <span className={styles.skipBannerText}>
          You can always invite more people later from the dashboard.
        </span>
        {/* <button type="button" className={styles.skipNowBtn} onClick={onSkip}>
          Skip for Now
        </button> */}
      </div>
    </div>
  );
};
